// Import trimestriel des études d'impact japonaises (assess.env.go.jp, ministère de l'Environnement, PDL 1.0) : le
// site refuse l'adresse du serveur du radar (403, constaté la nuit du 22/10/2026) mais répond à un poste et autorise
// les appels d'une autre origine. Le responsable des imports lance la lecture ici ; son navigateur lit la liste, puis
// le résumé des seuls dossiers que le worker retiendra, une requête par seconde, et dépose les dossiers lus. Le worker
// (apps/worker/src/radar/connectors/jpeia.py, `import_record`) applique ensuite toutes les règles de la source.

export const JPEIA_SITE = "https://assess.env.go.jp/";
const LIST_URL = "https://assess.env.go.jp/Assess/jireilist";
const DETAIL_URL = "https://assess.env.go.jp/Assess/jireidetail";
const PAGE_SIZE = 20;
const DELAY_MS = 1000;

// Règles de [jpeia] dans apps/worker/config/sources.toml, recopiées ici pour ne lire que les résumés utiles ; un test
// du worker (test_jpeia.py) vérifie qu'elles restent identiques.
export const JPEIA_YEARS = 5;
export const JPEIA_TYPES = ["河川", "発電所（水力）", "鉄道", "道路"] as const;
export const JPEIA_NATIONAL_ONLY = ["道路"] as const;
export const JPEIA_NAMED_WORDS = [
  "下水",
  "浄化センター",
  "水再生",
  "処理場",
  "ダム",
  "放水路",
  "調節池",
  "貯留",
  "トンネル",
  "地下鉄",
  "新幹線",
  "大橋",
  "橋梁",
  "架橋",
  "高架",
] as const;
export const JPEIA_EXCLUDED_STAGES = ["報告書", "事業廃止"] as const;
export const JPEIA_DETAIL_TYPES: Record<string, string> = {
  配慮書: "hairyo_gaiyou",
  方法書: "houhou",
  準備書: "junbi",
  評価書: "hyouka_gaiyou",
};
const NATIONAL_LAW = "環境影響評価法";

export type JpeiaRow = Record<string, unknown>;
export type JpeiaRecord = { row: JpeiaRow; detail: unknown | null };

/** Même forme normale que `jpeia.clean` : NFKC, espaces invisibles retirés, espaces réduits. */
export const clean = (value: unknown) =>
  String(value ?? "")
    .normalize("NFKC")
    .replace(/\u200b/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");

const TYPES = new Set(JPEIA_TYPES.map(clean));
const NATIONAL_ONLY = new Set(JPEIA_NATIONAL_ONLY.map(clean));
const NAMED = JPEIA_NAMED_WORDS.map(clean);
const EXCLUDED = new Set(JPEIA_EXCLUDED_STAGES.map(clean));
const DETAIL = new Map(
  Object.entries(JPEIA_DETAIL_TYPES).map(([stage, type]) => [
    clean(stage),
    type,
  ]),
);

/** Dossier que le worker retiendra (`jpeia.select`) : type ou nom d'ouvrage lu, étape avant les travaux. */
export const wanted = (row: JpeiaRow) => {
  const name = clean(row.jigyoumei);
  if (!name || !clean(row.jid) || EXCLUDED.has(clean(row.dankai))) return false;
  const national = clean(row.konkyo) === NATIONAL_LAW;
  const kinds = clean(row.jigyousyurui).split("、");
  if (
    kinds.some(
      (kind) => TYPES.has(kind) && (national || !NATIONAL_ONLY.has(kind)),
    )
  )
    return true;
  return NAMED.some((word) => name.includes(word));
};

export type JpeiaProgress = {
  phase: "list" | "detail";
  read: number;
  kept: number;
  details: number;
};

type Fetcher = (
  url: string,
) => Promise<{ ok: boolean; status: number; json: () => Promise<any> }>;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Lit la liste des dossiers des dernières années, puis le résumé de chaque dossier retenu. Un résumé indisponible
 * écarte le dossier, relu au trimestre suivant (comme la lecture directe du worker). */
export const readJpeia = async (
  today: Date,
  onProgress: (progress: JpeiaProgress) => void,
  fetcher: Fetcher = (url) => fetch(url),
  wait: (ms: number) => Promise<unknown> = sleep,
): Promise<{ records: JpeiaRecord[]; read: number; skipped: number }> => {
  // Le site lit « issyuyear » comme « cette année ou après ».
  const since = String(today.getFullYear() - JPEIA_YEARS + 1);
  const rows: JpeiaRow[] = [];
  let read = 0;
  for (let offset = 1; ; ) {
    const params = new URLSearchParams({
      mode: "jirei",
      keyword: "",
      limit: String(PAGE_SIZE),
      offset: String(offset),
      issyuyear: since,
    });
    const response = await fetcher(`${LIST_URL}?${params}`);
    if (!response.ok) throw new Error(`list:${response.status}`);
    const body = await response.json();
    const status = body?.ResultInfo?.Status;
    if (status !== 200) throw new Error(`list:${status}`);
    const page: JpeiaRow[] = body.Result ?? [];
    read += page.length;
    rows.push(...page.filter(wanted));
    onProgress({ phase: "list", read, kept: rows.length, details: 0 });
    if (page.length < PAGE_SIZE) break;
    offset += page.length;
    await wait(DELAY_MS);
  }
  const records: JpeiaRecord[] = [];
  let skipped = 0;
  for (const row of rows) {
    const type = DETAIL.get(clean(row.dankai));
    if (!type) {
      records.push({ row, detail: null });
      continue;
    }
    await wait(DELAY_MS);
    const params = new URLSearchParams({
      type,
      jid: clean(row.jid),
      reassess: row.reassess ? "1" : "0",
    });
    const response = await fetcher(`${DETAIL_URL}?${params}`);
    if (!response.ok) {
      skipped += 1;
      continue;
    }
    records.push({ row, detail: await response.json() });
    onProgress({
      phase: "detail",
      read,
      kept: rows.length,
      details: records.length,
    });
  }
  return { records, read, skipped };
};
